import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rrFetch } from "@/lib/rehearsalRoomAuth";

/**
 * Clients Restructure — Team 360. Mirrors clientRecordQueries.js's
 * `request()` error contract exactly (`.status`/`.body` on thrown errors).
 */
async function request(path, options = {}) {
  const res = await rrFetch(path, options);
  if (res.status === 204) return null;
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error((body && (body.detail?.error || body.detail?.message || body.detail)) || "request_failed");
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

const jsonPost = (path, payload) => request(path, { method: "POST", body: JSON.stringify(payload ?? {}) });

export const trqk = {
  overview: (wrkId) => ["team-record", "overview", wrkId],
  members: (wrkId) => ["team-record", "members", wrkId],
  sessions: (wrkId) => ["team-record", "sessions", wrkId],
  practice: (wrkId) => ["team-record", "practice", wrkId],
  files: (wrkId) => ["team-record", "files", wrkId],
  timeline: (wrkId) => ["team-record", "timeline", wrkId],
};

export function useCoachTeams() {
  return useQuery({
    queryKey: ["coach-teams"],
    queryFn: () => request("/api/rehearsal-room/coach/teams"),
  });
}

export function useTeamOverview(workspaceId) {
  return useQuery({
    queryKey: trqk.overview(workspaceId),
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/team-record/overview`),
  });
}

export function useTeamMembers(workspaceId) {
  return useQuery({
    queryKey: trqk.members(workspaceId),
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/team-record/members`),
  });
}

export function useTeamSessions(workspaceId) {
  return useQuery({
    queryKey: trqk.sessions(workspaceId),
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/team-record/sessions`),
  });
}

function _invalidateTeamSessions(qc, workspaceId) {
  qc.invalidateQueries({ queryKey: trqk.sessions(workspaceId) });
  qc.invalidateQueries({ queryKey: trqk.overview(workspaceId) });
  qc.invalidateQueries({ queryKey: ["coach-teams"] });
}

export function useAddTeamSessionCredits(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/team-record/entitlement/add`, payload),
    onSuccess: () => _invalidateTeamSessions(qc, workspaceId),
  });
}

export function useBookTeamSession(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/team-record/sessions/book`, payload),
    onSuccess: () => _invalidateTeamSessions(qc, workspaceId),
  });
}

export function useStartTeamSessionNow(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/team-record/sessions/start-now`, payload),
    onSuccess: () => _invalidateTeamSessions(qc, workspaceId),
  });
}

export function useTeamPractice(workspaceId) {
  return useQuery({
    queryKey: trqk.practice(workspaceId),
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/team-record/practice`),
  });
}

export function useTeamFiles(workspaceId) {
  return useQuery({
    queryKey: trqk.files(workspaceId),
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/team-record/files`),
  });
}

export function useTeamTimeline(workspaceId) {
  return useQuery({
    queryKey: trqk.timeline(workspaceId),
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/team-record/timeline`),
  });
}
